import {
    ApolloError,
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { UnsavedChanges } from '@deltek/specification-client/core/Constants/messages'
import NavPrompt, {
    NavPromptMessageProps,
} from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import {
    CurrentUserFirmsQuery,
    CurrentUserInfoQuery,
} from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import {
    CurrentUserFirmsResponse,
    CurrentUserInfoResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    FirmId,
    FirmResponse,
} from '@deltek/specification-client/core/Models/firm'
import { FormProps } from '@deltek/specification-client/core/Models/formProps'
import {
    AssignedUser,
    ListingDetail,
} from '@deltek/specification-client/core/Models/listing'
import { LogEventSource } from '@deltek/specification-client/core/Models/listingSearch'
import {
    ContentLibrary,
    ListingLevel,
    ManufacturerProductImageDetail,
    ManufacturerProductPropertyDetail,
    ManufacturerProductVideoDetail,
    ManufacturerProductWebLinkDetail,
    Partnership,
} from '@deltek/specification-client/core/Models/product'
import {
    ListingDetailCatalogInput,
    ListingDetailDocumentInput,
    ListingDetailImageInput,
    ListingDetailLinkInput,
    ListingDetailPartnershipLinkInput,
    ListingDetailVideoInput,
    ProductListingDetail,
    ProductListingDetailInput,
    ProductListingDetailPayload,
} from '@deltek/specification-client/core/Models/productListingDetail'
import { User } from '@deltek/specification-client/core/Models/user'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    navigateToExternalLink,
    validateFullUrlPath,
} from '@deltek/specification-client/core/Utilities/urlUtils'
import ViewDocuments from '@deltek/specification-client/core/Widgets/ProductListing/Details/Documents/ViewDocuments'
import ViewLinks from '@deltek/specification-client/core/Widgets/ProductListing/Details/Links/ViewLinks'
import ViewPartnerships from '@deltek/specification-client/core/Widgets/ProductListing/Details/Partnerships/ViewPartnerships'
import {
    getDocDetail,
    getNormalizedProperties,
} from '@deltek/specification-client/core/Widgets/ProductListing/Details/productDetailUtils'
import Backdrop from '@material-ui/core/Backdrop'
import CircularProgress from '@material-ui/core/CircularProgress'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { GetAllUsersQuery } from 'api/graphQL/queries/user.queries'
import {
    CreateProductListingDetail,
    GenerateProductSpecification,
    SaveProductListingDetail,
    SetProductListingDetailPartnership,
} from 'bpm/api/graphQL/mutations/productListing.mutation'
import {
    FirmListingUsersQuery,
    FirmListingUsersQueryCountOnly,
    FirmQuery,
    FirmResellerStatusQuery,
} from 'bpm/api/graphQL/queries/firm.queries'
import { GetAllProducts } from 'bpm/api/graphQL/queries/productSearch.queries'
import { ProductSearchResponse } from 'bpm/api/graphQL/types/product.types'
import { AllUsersResponse } from 'bpm/api/graphQL/types/users.type'
import {
    isDeltekUserorAdmin,
    isUserBPMAdmin,
} from 'bpm/pages/ProductsPage/productsUtils'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, {
    Context,
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import ImageGallery from 'react-image-gallery'
import { useHistory, useLocation } from 'react-router-dom'
import { EditDocuments } from './Documents/EditDocuments'
import { EditLinks } from './Links/EditLinks'
import {
    UserAndProductInfo,
    UserRole,
    getRoleBasedAccess,
} from './ListingDetailsUtils'
import { ListingHeader } from './ListingHeader'
import { AUTOGEN_ELIGIBLE_STATUSES, ListingStatus } from './ListingHeaderVariants/ListingStatusVariant'
import {
    resetPDNStore,
    resetTempNotes,
} from './ListingNotes/ListingNotes.store'
import usePDNhook from './ListingNotes/hooks/usePDNHook'
import ListingDetailMediaCarousel from './Media/ListingDetailMediaCarousel'
import { EditPartnershipSwatchbox } from './Partnerships/EditPartnershipSwatchbox'
import { EditPartnerships } from './Partnerships/EditPartnerships'
import { ProductDetails } from './ProductDetails'
import {
    ProductProperty,
    ProductPropertyOption,
} from './Properties/PremiumPlus/NonMasterSpec/nonMSTypes'
import { getSelectedProperties } from './Properties/PremiumPlus/pplUtils'

// Styles

const useStyles = makeStyles((theme: Theme) => ({
    basicListing: { backgroundColor: '#343334' },
    header: {
        minHeight: 108,
        color: theme.palette.secondary.light,
        marginLeft: theme.spacing(-3),
        width: 'calc(100% + 48px)',
    },
    basicListContainer: {
        display: 'flex',
        height: 400,
        color: theme.palette.secondary.light,
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: pxToRem(72),
        [theme.breakpoints.down('sm')]: { marginRight: 0 },
    },
    imageGalleryFlexContainer: {
        [theme.breakpoints.down('sm')]: {
            borderBottom: '1px solid #c9c9c9',
        },
    },
    linksContainer: {
        paddingTop: theme.spacing(1),
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff',
    },
}))

// Types

export interface ExtendedListingDetail extends ListingDetail {
    assignedContactUsers: Partial<User>[]
}
export interface ListingDetailsProps {
    firmId?: string
    productInfo: Partial<ExtendedListingDetail>
    handleProductInfoUpdate: (value: Partial<ExtendedListingDetail>) => void
}

export type ListingHeaderVariants =
    | 'status'
    | 'state'
    | 'listingLevel'
    | 'notes'

export type ProductListingDetailFormField =
    | 'model'
    | 'name'
    | 'description'
    | 'buildingTypes'
    | 'links'
    | 'partnerships'
    | 'assignedBPMUsers'
    | 'assignedContactUsers'
    | 'assignedDeltekUsers'
    | 'categories'
    | 'contentLibrary'

export type SupportedOptionsObject = Record<
    string,
    Record<string, Array<string>>
>

export type PropertyType = Partial<ProductProperty>[]

export type PropertiesType = Record<string, PropertyType>

export interface ProductListingDetailForm {
    productName: FormProps
    productDescription: FormProps
    model: FormProps
    contentLibrary: FormProps
    buildingTypes: {
        value: string[]
        valid: boolean
    }
    categories: {
        value: string[]
        valid: boolean
    }
    assignedBPMUsers: {
        value: Partial<AssignedUser>[]
        valid: boolean
    }
    assignedContactUsers: {
        value: string
        valid: boolean
    }
    assignedDeltekUsers: {
        value: Partial<AssignedUser>[]
        valid: boolean
    }
    webLinks: {
        value: Partial<ListingDetailLinkInput>[]
        valid: boolean
    }
    partnerships: {
        value: Partial<ListingDetailPartnershipLinkInput>[]
        valid: boolean
    }
    media: {
        images: Partial<ListingDetailImageInput>[]
        videos: Partial<ListingDetailVideoInput>[]
    }
    catalogs: {
        value?: Partial<ListingDetailCatalogInput>[]
        valid: boolean
    }
    documents: {
        value?: Partial<ListingDetailDocumentInput>[]
        valid: boolean
    }
    properties: {
        normalizedProperties: PropertiesType
        pristineValue: SupportedOptionsObject
        value: SupportedOptionsObject
        valid: boolean
    }
}

export interface ProductContextType {
    selectedData: SupportedOptionsObject
    properties: PropertiesType
    setProductDetailFormProperties: (
        properties: ProductListingDetailForm['properties']['normalizedProperties'],
        value: SupportedOptionsObject
    ) => void
    product: Partial<ListingDetail>
    handleSelection: (
        propertyName: string,
        subPropertyName: string,
        value: string
    ) => () => void
    setSelectedData: Dispatch<SetStateAction<ProductListingDetailForm>>
    updateListingStates: (
        saveProductListingDetail: Partial<ProductListingDetail>
    ) => void
    firmContact: Array<Partial<User>>
    isPremiumAuthorized: boolean
    isManufacturerOptionsReadOnly: boolean
}

// String Variables

const savedChanges =
    'Changes to the listing will be saved and the listing will be ready for BPM submissions. Are you sure you want to save the product listing?'
const bpmUnsubmitted =
    'You have saved unsubmitted changes on the listing. Deltek will not respond to your changes until these changes are submitted. Are you sure you want to cancel?'
const bpmSavedChanges =
    'Changes will be saved and submitted for Deltek review. Are you sure you want to submit?'
const deprecatedMsg =
    'This action will change a deprecated product and its original Family and make this product viewable in its original Family and Product Type.  Select this option carefully.'
const MAX_WORD_LIMIT = 10000

export const SUSTAINABLE_MIND_BASE_URL = 'https://www.transparencycatalog.com'
export const TRANSPARENCY_CATALOG_PLACEHOLDER =
    'Add the company name found in the Transparency Catalog.'

// Util Functions

const getPropertiesInput = (
    properties: PropertiesType,
    value: SupportedOptionsObject
) => {
    let dataSetForMutation: any = []
    const sortedProperties = [
        ...Object.keys(properties).filter((prop) => !prop),
        ...Object.keys(properties).filter((prop) => !!prop),
    ]
    sortedProperties.forEach((propertyName) => {
        properties[propertyName]?.forEach(
            (sub: Partial<ManufacturerProductPropertyDetail>) => {
                const subPropName =
                    (sub.originalPropertyName && '|' + sub.subPropertyName) ||
                    sub.subPropertyName
                const propertyNameValue =
                    (sub.originalPropertyName &&
                        sub.originalPropertyName.split(' / ').join('|') +
                            subPropName) ||
                    propertyName.split(' / ').join('|') + subPropName
                const optionGroups = sub.optionGroups?.flatMap((grp) => {
                    const options = grp.options?.length
                        ? grp.options.map(
                              ({
                                  isOther,
                                  isPremium,
                                  isPremiumPlus,
                                  optionId,
                                  value,
                                  isUnitOfMeasure = false,
                                  imperialUnits = '',
                                  metricUnits = '',
                                  isInlineTextOnly,
                              }: Partial<ProductPropertyOption>) => ({
                                  isOther,
                                  isPremium,
                                  isPremiumPlus,
                                  optionId,
                                  value,
                                  isUnitOfMeasure,
                                  imperialUnits,
                                  metricUnits,
                                  isInlineTextOnly,
                              })
                          )
                        : []

                    return {
                        prefix: grp.prefix,
                        postfix: grp.postfix,
                        groupId: grp.groupId,
                        options,
                    }
                })

                dataSetForMutation.push({
                    propertyId: sub.propertyId!,
                    propertyName: propertyNameValue,
                    optionGroups,
                    isPremiumPlus: sub.isPremiumPlus,
                    supportedOptionIds:
                        (value[propertyName] &&
                            value[propertyName][
                                sub.subPropertyName as string
                            ]) ??
                        [],
                })
            }
        )
    })
    return dataSetForMutation
}

export const initialFormValues = (
    productInfo: Partial<ExtendedListingDetail> = {},
    firmContact: Partial<User>[] = []
) => ({
    productName: {
        value: productInfo.productName ?? '',
        required: true,
        valid:
            !!productInfo.productName ||
            (productInfo.submissionStatus === ListingStatus.Completed &&
                productInfo.type === ListingLevel.Manufacturer),
    },
    productDescription: {
        value: productInfo.longDescription ?? '',
        required: false,
        valid: true,
    },
    model: {
        value: productInfo.model ?? '',
        required: false,
        valid: true,
    },
    contentLibrary: {
        value: productInfo.contentLibrary ?? '',
        required: false,
        valid: true,
    },
    buildingTypes: {
        value: productInfo.buildingTypes ?? [],
        valid: (productInfo.buildingTypes ?? []).length > 0,
    },
    categories: {
        value: productInfo.categories ?? [],
        valid: (productInfo.categories ?? []).length > 0,
    },
    webLinks: {
        value: (productInfo.webLinks?.map((each) => ({
            order: each.order,
            type: each.type,
            url: each.url,
        })) ?? []) as ListingDetailLinkInput[],
        valid: true,
    },
    partnerships: {
        value: (productInfo.partnershipLinks?.map((each) => ({
            name: each.name,
            partnerType: each.partnerType,
            url:
                each.url && each.name === 'Sustainable Minds'
                    ? each.url.replace(
                          `${SUSTAINABLE_MIND_BASE_URL}/company/`,
                          ''
                      )
                    : each.url ?? '',
        })) ?? []) as ListingDetailPartnershipLinkInput[],
        valid: true,
    },
    media: {
        images: (productInfo.images?.map((each) => ({
            id: each.id,
            isPrimary: each.isPrimary,
            order: each.order,
            originalFilename: each.originalFilename,
            url: each.filename,
        })) ?? []) as ListingDetailImageInput[],
        videos: (productInfo.videos?.map((each) => ({
            fromSource: each.fromSource,
            id: each.id,
            order: each.order,
            videoId: each.videoId,
            videoStillImageUrl: each.videoStillImageUrl,
        })) ?? []) as ListingDetailVideoInput[],
        valid: true,
    },
    catalogs: {
        value: undefined,
        valid: true,
    },
    documents: {
        value: productInfo.manufacturerDocs?.map((each) => ({
            fileName: each.path,
            docType: each.docType,
            docVariation: each.docVariation,
            expirationDate: each.expirationDate,
            id: each.id,
            title: each.title,
        })) as Partial<ListingDetailDocumentInput>[],
        valid: true,
    },
    properties: {
        normalizedProperties: getNormalizedProperties(productInfo?.properties),
        pristineValue: getSelectedProperties(productInfo?.properties),
        value: getSelectedProperties(productInfo?.properties),
        valid: true,
    },
    assignedBPMUsers: {
        value: (productInfo.assignedBPMUsers?.map((each) => ({
            userId: each?.id,
            role: each?.role,
        })) ?? []) as Partial<AssignedUser>[],
        valid: true,
    },
    assignedContactUsers: {
        value: JSON.stringify(
            (productInfo.assignedContactUsers?.length &&
                productInfo.assignedContactUsers) ||
                firmContact
        ),

        valid: true,
    },
    assignedDeltekUsers: {
        value: (productInfo.assignedDeltekUsers?.map((each) => ({
            userId: each?.id,
            role: each?.role,
        })) ?? []) as Partial<AssignedUser>[],
        valid: true,
    },
})

export const ProductContext: Context<Partial<
    ProductContextType
>> = createContext({})

export const ListingDetailsPage: React.FC<ListingDetailsProps> = ({
    firmId,
    productInfo,
    handleProductInfoUpdate,
}) => {
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const { data: firmResponse, loading: firmResponseLoading } = useQuery<
        FirmResponse
    >(FirmQuery, {
        variables: {
            id:
                currentUserInfoResponse?.currentUserInfo?.account?.accountId ??
                '',
        },
        skip: !currentUserInfoResponse,
    })

    const { data: firmMembershipsResponse } = useQuery<CurrentUserFirmsResponse>(
        CurrentUserFirmsQuery
    )

    const primaryFirmId = firmMembershipsResponse?.myFirmMemberships?.find(
        (m) => m.isPrimary
    )?.firmId

    const { data: primaryFirmResponse } = useQuery<FirmResponse, { id: string }>(
        FirmResellerStatusQuery,
        {
            variables: { id: primaryFirmId ?? '' },
            skip:
                !primaryFirmId ||
                primaryFirmId ===
                    currentUserInfoResponse?.currentUserInfo?.account?.accountId,
        }
    )

    const [
        generateProductSpecification,
        { data: prodSpecificationData, loading: spfDocLoading },
    ] = useMutation<any>(GenerateProductSpecification)

    const [canAutoGeneratePSD, setCanAutoGeneratePSD] = useState<boolean>(
        !!productInfo.canAutoGenerateProductSpecification
    )

    const { tempNotes, refetchNotes } = usePDNhook()

    const {
        isDeltekUser,
        isDeltekAdmin,
        isDeltekPublishingAuthor,
        isDeltekAuthor,
        isDeltekReviewer,
    } = isDeltekUserorAdmin(currentUserInfoResponse)

    const { isBPM, isBPMAdmin, isBPMAuthor, isBPMReviewer } = isUserBPMAdmin(
        currentUserInfoResponse
    )

    //Reseller on the Reseller firm
    const isBPMReseller = firmResponse?.firm.isReseller ?? false

    //Reseller as guest on the BPM firm
    const activeFirmMembership = firmMembershipsResponse?.myFirmMemberships?.find(
        (m) =>
            m.firmId ===
            currentUserInfoResponse?.currentUserInfo?.account?.accountId
    )
    const isGuestBPMReseller =
        (isBPMAdmin || isBPMAuthor) &&
        productInfo.firmId ===
            currentUserInfoResponse?.currentUserInfo?.account?.accountId &&
        activeFirmMembership?.isPrimary === false &&
        (primaryFirmResponse?.firm.isReseller ?? false)

    const isBPMListing =
        (isBPMAdmin || isBPMAuthor || isBPMReviewer) &&
        productInfo.firmId ===
            currentUserInfoResponse?.currentUserInfo?.account?.accountId

    const getCurrentRole = () => {
        const firmOwned =
            productInfo.firmId ===
            currentUserInfoResponse?.currentUserInfo?.account?.accountId

        return (
            (isBPMAdmin && firmOwned && UserRole.BPMAdmin) ||
            (isBPMAuthor && firmOwned && UserRole.BPMAuthor) ||
            (isBPMReviewer && firmOwned && UserRole.BPMReviewer) ||
            (isDeltekAdmin && UserRole.DeltekAdmin) ||
            (isDeltekPublishingAuthor && UserRole.DeltekPublishingAuthor) ||
            (isDeltekAuthor && UserRole.DeltekAuthor) ||
            (isDeltekReviewer && UserRole.DeltekReviewer) ||
            (isBPMReseller &&
                !isBPMReviewer &&
                !isDeltekReviewer &&
                !firmOwned &&
                UserRole.BPMReseller) ||
            UserRole.Others
        )
    }

    // This is the object that we will send for mutating the product listing
    const [mfrProductDetailUpdate, setMfrProductDetailUpdate] = useState<
        Partial<ExtendedListingDetail>
    >(productInfo)

    // Only Deltek users and members of the listing's firm should attempt to query firm users; others aren't authorized.
    const currentRole = getCurrentRole()
    const isAuthorizedToGetUsers = ![
        UserRole.Others,
        UserRole.BPMReseller,
    ].includes(currentRole)
    const usersQueryToUse = isAuthorizedToGetUsers
        ? FirmListingUsersQuery
        : FirmListingUsersQueryCountOnly
    const { data: firmUsersData } = useQuery<Partial<FirmResponse>, FirmId>(
        usersQueryToUse,
        {
            variables: {
                firmId: firmId ?? '',
            },
            skip: !!!firmId, // don't query if we don't have a firm ID
        }
    )

    const { data: allUsersData } = useQuery<Partial<AllUsersResponse>, FirmId>(
        GetAllUsersQuery
    )
    const [
        getAllProducts,
        { data: allProductsName, loading: checkingDuplicacy },
    ] = useLazyQuery<ProductSearchResponse>(GetAllProducts, {
        displayName: 'GetAllProducts',
        fetchPolicy: 'cache-and-network',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingDetailsPage_ProductSearchError`,
                },
            })
        },
    })

    const roleInput: UserAndProductInfo = {
        isactive: productInfo.isActive,
        listingType: productInfo.type,
        status: productInfo.submissionStatus as ListingStatus,
        userRole: currentRole as UserRole,
    }
    const roleBasedAccess = getRoleBasedAccess(roleInput)

    const { mFGOnly, mFG, submit } = roleBasedAccess
    const isMFGAdded = mFG

    const [addProduct, setAddProduct] = useState<boolean>(isMFGAdded)
    const [isAddProductActive, setIsAddProductActive] = useState(false)
    const [newListingRequest, setNewListingRequest] = useState<boolean>(false)
    const [editMode, setEditMode] = useState<boolean>(false)
    const [editResellerMode, setEditResellerMode] = useState<boolean>(false)
    const [bpmSave, setBpmSave] = useState<boolean>(false)
    const [cancelled, setCancelled] = useState<boolean>(false)
    const [dirty, setDirty] = useState<boolean>(false)
    const [propertiesDirty, setPropertiesDirty] = useState<boolean>(false)
    const [duplicateName, setDuplicateName] = useState<boolean>(false)
    const newListingDataRef = useRef<Partial<ExtendedListingDetail>>({})
    const queryParams = new URLSearchParams(useLocation().search)
    const carouselRef = useRef<ImageGallery | null>(null)
    const hasMountedRef = useRef(false)
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const designatedAccountAdminContact = useMemo(() => {
        return (
            (firmUsersData?.firm?.designatedAccountAdminUser && [
                firmUsersData.firm.designatedAccountAdminUser,
            ]) ||
            []
        )
    }, [firmUsersData])

    const [productListingDetailForm, setProductListingDetailForm] = useState<
        ProductListingDetailForm
    >(initialFormValues(productInfo, designatedAccountAdminContact))
    const history = useHistory()
    const [filestackLoading, setFilestackLoading] = useState<boolean>(false)
    const [transferringFiles, setTransferringFiles] = useState<boolean>(false)

    const hasManufacturerOptions = useMemo(
        () =>
            !!Object.values(
                productListingDetailForm.properties.normalizedProperties
            ).some(
                (properties) =>
                    !!properties.some(
                        (property) =>
                            !!property?.optionGroups?.some(
                                (optGrp) =>
                                    !!optGrp.options?.some(
                                        (option) => !!option.isPremiumPlus
                                    )
                            )
                    )
            ),
        [productListingDetailForm.properties]
    )

    //SaveProductListingDetail
    const [
        createProductListingDetail,
        { loading: newProductListingDetailLoading },
    ] = useMutation<
        { createProductListingDetail: Partial<ProductListingDetailPayload> },
        { saveProductListingDetailInput: Partial<ProductListingDetailInput> }
    >(CreateProductListingDetail, {
        onError: (error: ApolloError) => {
            let erMsg =
                error.message ??
                'An error occured while trying to save changes. Please try again.'
            if (error.message.includes('already exists'))
                erMsg =
                    'Duplicate product name exists, choose another name or cancel.'
            showGlobalSnackbar({
                variables: {
                    message: erMsg,
                    variant: 'error',
                    id: 'ProductListing_DetailUpdateError',
                },
            })
            return null
        },
        onCompleted: ({
            createProductListingDetail,
        }: {
            createProductListingDetail: Partial<ProductListingDetailPayload>
        }) => {
            const listing = createProductListingDetail.listingDetail ?? {}
            resetPDNStore()
            setPropertiesDirty(false)
            showGlobalSnackbar({
                variables: {
                    message: 'Listing created successfully',
                    variant: 'success',
                    id: 'ProductListing_NewListingSuccess',
                },
            })
            setEditMode(false)
            setNewListingRequest(false)
            setIsAddProductActive(false)
            setAddProduct(listing.type === ListingLevel.Manufacturer)
            updateListingStates(listing)
            history.push({
                ...history.location,
                pathname: `/bpm/products/product/listing/${listing.listingId}`,
            })
        },
    })

    const [
        saveProductListingDetail,
        { loading: saveProductListingDetailLoading },
    ] = useMutation<
        { saveProductListingDetail: Partial<ProductListingDetailPayload> },
        { saveProductListingDetailInput: Partial<ProductListingDetailInput> }
    >(SaveProductListingDetail, {
        // prevents error overlay
        // https://github.com/apollographql/apollo-client/issues/5708#issuecomment-569754975
        onError: (error: ApolloError) => {
            handleEditClick()
            let erMsg =
                error.message ??
                'An error occured while trying to save changes. Please try again.'
            if (error.message.includes('already exists'))
                erMsg =
                    'Duplicate product name exists, choose another name or cancel.'
            if (error.message.includes('Deprecated')) erMsg = error.message
            showGlobalSnackbar({
                variables: {
                    message: erMsg,
                    variant: 'error',
                    id: 'ProductListing_DetailUpdateError',
                },
            })
            resetTempNotes()
            return null
        },
        onCompleted: ({
            saveProductListingDetail,
        }: {
            saveProductListingDetail: Partial<ProductListingDetailPayload>
        }) => {
            resetPDNStore()
            updateListingStates(saveProductListingDetail.listingDetail ?? {})
            setPropertiesDirty(false)
            showGlobalSnackbar({
                variables: {
                    message: 'Saved successfully',
                    variant: 'success',
                    id: 'ProductListing_DetailUpdateSuccess',
                },
            })
        },
    })

    const [saveSwatchbox, { loading: saveSwatchboxLoading }] = useMutation<{
        setProductListingDetailPartnership: Partial<ProductListingDetail>
        setProductDetailPartnershipInput: Partial<
            ListingDetailPartnershipLinkInput
        >[]
    }>(SetProductListingDetailPartnership, {
        onCompleted: ({
            setProductListingDetailPartnership,
        }: {
            setProductListingDetailPartnership: Partial<ProductListingDetail>
        }) => {
            resetPDNStore()
            updateListingStates(setProductListingDetailPartnership)
            setPropertiesDirty(false)
            setEditResellerMode(false)
            showGlobalSnackbar({
                variables: {
                    message: 'Saved successfully',
                    variant: 'success',
                    id: 'ProductListing_DetailUpdateSuccess',
                },
            })
        },
    })

    const updateListingStates = (
        saveProductListingDetail: Partial<ProductListingDetail>
    ) => {
        const savedBPMUsers =
            saveProductListingDetail?.assignedBPMUsers
                ?.map((selectedBPMUser) => {
                    const userProfile = firmUsersData?.firm?.users?.find(
                        (user) => user.id === selectedBPMUser.userId
                    )
                    return userProfile as Partial<AssignedUser>
                })
                .filter((selectedBPMUser) => !!selectedBPMUser) ?? []

        const savedDeltekUsers =
            isDeltekAdmin || isDeltekAuthor
                ? saveProductListingDetail?.assignedDeltekUsers
                      ?.map((assignedDeltekUser) => {
                          const userProfile = allUsersData?.getAllUsers?.users?.find(
                              (user) => user.id === assignedDeltekUser.userId
                          )
                          return userProfile as Partial<AssignedUser>
                      })
                      .filter((assignedDeltekUser) => !!assignedDeltekUser) ??
                  []
                : productInfo.assignedDeltekUsers ?? []

        const assignedContactUsers =
            saveProductListingDetail?.assignedContactUsers
                ?.map((user) => {
                    const userWithProfile = firmUsersData?.firm?.users?.find(
                        (firmUser) => firmUser.id === user.userId
                    )
                    return userWithProfile as Partial<AssignedUser>
                })
                .filter((user) => !!user) ?? designatedAccountAdminContact

        const updatedListingData = {
            firmLogoUrl: productInfo.firmLogoUrl,
            firmName: productInfo?.firmName ?? '',
            sectionName: productInfo?.sectionName ?? '',
            ...saveProductListingDetail,
            assignedContactUsers,
            contentLibrary:
                ContentLibrary[
                    (saveProductListingDetail?.contentLibrary ??
                        'MasterSpec') as keyof typeof ContentLibrary
                ],
        } as Partial<ExtendedListingDetail>
        handleProductInfoUpdate({
            ...updatedListingData,
            listingId: saveProductListingDetail.listingId,
            images: saveProductListingDetail.images,
            catalogs: saveProductListingDetail.catalogs,
            manufacturerDocs: saveProductListingDetail.documents,
            properties: saveProductListingDetail.properties,
            videos: saveProductListingDetail.videos,
            assignedBPMUsers: savedBPMUsers ?? [],
            assignedDeltekUsers: savedDeltekUsers ?? [],
        })
        setProductListingDetailForm(
            initialFormValues(
                {
                    ...updatedListingData,
                    images: saveProductListingDetail.images,
                    videos: saveProductListingDetail.videos,
                    catalogs: saveProductListingDetail.catalogs,
                    manufacturerDocs: saveProductListingDetail.documents,
                    properties: saveProductListingDetail.properties,
                    assignedBPMUsers: savedBPMUsers ?? [],
                    assignedDeltekUsers: savedDeltekUsers ?? [],
                },
                designatedAccountAdminContact
            )
        )

        setMfrProductDetailUpdate({
            ...updatedListingData,
            images: saveProductListingDetail.images,
            catalogs: saveProductListingDetail.catalogs,
            manufacturerDocs: saveProductListingDetail.documents,
            properties: saveProductListingDetail.properties,
            videos: saveProductListingDetail.videos,
            assignedBPMUsers: saveProductListingDetail.assignedBPMUsers ?? [],
            assignedDeltekUsers:
                saveProductListingDetail.assignedDeltekUsers ?? [],
        })
    }
    const classes = useStyles()
    const isBasic = productInfo.type === ListingLevel.Manufacturer

    const hasErrors =
        Object.values(productListingDetailForm).find((each) => !each.valid) !==
        undefined

    const handleAddProductClick = () => {
        setIsAddProductActive(true)
        if (!addProduct && productInfo.type === ListingLevel.Manufacturer) {
            setAddProduct(true)
            setEditMode(true)
            setProductListingDetailForm((past) => ({
                ...past,
                buildingTypes: {
                    ...past.buildingTypes,
                    valid: true,
                },
                categories: {
                    ...past.categories,
                    valid: true,
                },
            }))
        } else {
            const blankTemplate = initialFormValues({
                properties: productInfo.properties?.map((prop) => ({
                    ...prop,
                    optionGroups: prop.optionGroups?.map((grps) => ({
                        ...grps,
                        options: grps.options?.map((ops) => ({
                            ...ops,
                            isSupported: false,
                        })),
                    })),
                    options: prop.options?.map((ops) => ({
                        ...ops,
                        isSupported: false,
                    })),
                })),
            })
            !addProduct && setAddProduct(true)
            setNewListingRequest(true)
            //Manufacturer, Category, Family number, Family name, and Product Type
            const {
                firmName,
                categories,
                sectionNumber,
                sectionName,
                productType,
                properties,
                firmId,
                type,
                isActive,
                submissionStatus,
                sectionTitle,
                firmLogoUrl,
            } = { ...productInfo }
            const newListingData: Partial<ExtendedListingDetail> = {
                sectionTitle,
                firmLogoUrl,
                firmId,
                firmName,
                categories,
                sectionNumber,
                sectionName,
                productType,
                properties,
                type,
                submissionStatus,
                isActive,
                assignedBPMUsers: [],
                assignedDeltekUsers: [],
                assignedUser: '',
                bimLinks: [],
                buildingTypes: [],
                catalogs: [],
                images: [],
                longDescription: '',
                partnershipLinks: [],
                manufacturerDocs: [],
                model: '',
                productName: '',
                webLinks: [],
            }
            setMfrProductDetailUpdate({ ...newListingData })
            newListingDataRef.current = { ...newListingData }
            setEditMode(true)

            setProductListingDetailForm((past) => ({
                ...blankTemplate,
                buildingTypes: {
                    ...blankTemplate.buildingTypes,
                    valid: true,
                },
                categories: {
                    ...past.categories,
                    valid: true,
                },
                productName: {
                    ...blankTemplate.productName,
                    valid:
                        newListingData.submissionStatus ===
                            ListingStatus.Completed &&
                        newListingData.type === ListingLevel.Manufacturer,
                },
            }))
        }
    }

    const handleCancelClick = () => {
        if (dirty || propertiesDirty || bpmSave) {
            NavPromptDialogContainer(
                JSON.stringify({
                    title: 'Manage Listing',
                    preferredMessage: bpmSave ? bpmUnsubmitted : UnsavedChanges,
                    buttonColor: '#2D9CDB',
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState: boolean) => {
                    if (!callbackState) return

                    resetPDNStore()
                    if (newListingRequest) setNewListingRequest(false)
                    handleBPMUsersChange(
                        productInfo?.assignedBPMUsers ?? [],
                        true
                    )
                    handleDeltekUsersChange(
                        productInfo?.assignedDeltekUsers ?? [],
                        true
                    )
                    resetFormStates()
                }
            )
            return
        } else if (!propertiesDirty && newListingRequest) {
            setNewListingRequest(false)
        }
        resetFormStates()
    }

    const resetFormStates = () => {
        setIsAddProductActive(false)
        setEditMode(false)
        setEditResellerMode(false)
        setAddProduct(isMFGAdded)
        setMfrProductDetailUpdate(productInfo)
        setProductListingDetailForm(
            initialFormValues(productInfo, designatedAccountAdminContact)
        )
        setCancelled(true)
        setBpmSave(false)
        setDuplicateName(false)
        setCanAutoGeneratePSD(!!productInfo.canAutoGenerateProductSpecification)
    }

    const handleEditClick = () => {
        if (
            currentRole === UserRole.BPMReseller &&
            !!Number(processEnv.REACT_APP_ShowSwatchbox)
        )
            setEditResellerMode(true)
        else setEditMode(true)
        setCancelled(false)
        setMfrProductDetailUpdate(productInfo)
        if (mFG && !isBPM) {
            setProductListingDetailForm((past) => ({
                ...past,
                buildingTypes: {
                    ...past.buildingTypes,
                    valid: true,
                },
            }))
        }
    }

    const handleSubmission = (shouldSubmit: boolean) => {
        if (submit && shouldSubmit) {
            if (mfrProductDetailUpdate.type?.includes('Premium')) {
                if (
                    mfrProductDetailUpdate.type !==
                    ListingLevel.PremiumProprietary
                ) {
                    return (
                        mfrProductDetailUpdate.type + ' Review - BPM Submitted'
                    )
                } else {
                    return 'Proprietary Review - BPM Submitted'
                }
            } else {
                // Non-Premium BPM Submit must not change the status — appending '-BPM Submitted' produced
                // invalid enum values (e.g. 'In Review - BPM Submitted') that locked the listing in unknown state.
                return mfrProductDetailUpdate.submissionStatus
            }
        } else {
            return mfrProductDetailUpdate.submissionStatus
        }
    }

    const handleSaveClick = (shouldSubmit = false) => {
        if (checkingDuplicacy) return

        let savedBPMUsers: Partial<AssignedUser>[] = []
        let saveDeltekUsers: Partial<AssignedUser>[] = []
        if (currentRole === UserRole.BPMReseller) {
            NavPromptDialogContainer(
                JSON.stringify({
                    title: 'Manage Listing',
                    preferredMessage:
                        'Changes to Partnerships will be saved. Are you sure you want to save?',
                    buttonColor: '#2D9CDB',
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState: boolean) => {
                    if (!callbackState) return
                    saveSwatchbox({
                        variables: {
                            setProductListingDetailPartnershipInput: {
                                listingId: productInfo.listingId,
                                partnershipInfo: productListingDetailForm.partnerships.value.filter(
                                    (p) =>
                                        p.name === 'Swatchbox' ||
                                        p.name === 'BIMsmith'
                                ) as Partial<
                                    ListingDetailPartnershipLinkInput
                                >[],
                            },
                        },
                    })
                }
            )
            return
        }

        productListingDetailForm.assignedBPMUsers?.value.forEach((item) =>
            item.userId
                ? savedBPMUsers.push({
                      userId: item.userId,
                      role: item.role,
                  })
                : savedBPMUsers.push({ userId: item.id, role: item.role })
        )
        productListingDetailForm.assignedDeltekUsers?.value.forEach((item) =>
            item.userId
                ? saveDeltekUsers.push({
                      userId: item.userId,
                      role: item.role,
                  })
                : saveDeltekUsers.push({
                      userId: item.id,
                      role: item.role,
                  })
        )
        const assignedContacts =
            productListingDetailForm.assignedContactUsers?.value
        let assignedContactUsers = (assignedContacts
            ? JSON.parse(assignedContacts)
            : designatedAccountAdminContact) as Partial<AssignedUser>[]

        assignedContactUsers = assignedContactUsers.map((item) => ({
            userId: item.id,
            role: item.role,
        }))
        if (
            !hasErrors &&
            productListingDetailForm.assignedBPMUsers?.value.length ===
                savedBPMUsers.length
        ) {
            if (!duplicateName) {
                if (!submit && (isBPM || isDeltekPublishingAuthor)) {
                    processFormSubmission(
                        shouldSubmit,
                        savedBPMUsers,
                        saveDeltekUsers,
                        assignedContactUsers
                    )
                    return
                }
                const isDeprecated =
                    productInfo.submissionStatus === ListingStatus.Deprecated
                NavPromptDialogContainer(
                    JSON.stringify({
                        title: 'Manage Listing',
                        preferredMessage:
                            submit && shouldSubmit
                                ? bpmSavedChanges
                                : isBPM
                                ? 'Changes will be saved. Once the information is complete, remember to submit the listing to prepare it for the next step in the review process.'
                                : isDeprecated
                                ? deprecatedMsg
                                : savedChanges,
                        buttonColor: '#2D9CDB',
                        confirmText: 'Yes',
                        cancelText: 'No',
                    } as NavPromptMessageProps),
                    (callbackState: boolean) => {
                        if (!callbackState) return
                        processFormSubmission(
                            shouldSubmit,
                            savedBPMUsers,
                            saveDeltekUsers,
                            assignedContactUsers
                        )
                    }
                )
            } else
                showGlobalSnackbar({
                    variables: {
                        message:
                            'Duplicate product name exists, choose another name or cancel.',
                        variant: 'error',
                        id: 'ProductListing_DetailUpdateError',
                    },
                })
        }
    }

    const processFormSubmission = (
        shouldSubmit = false,
        savedBPMUsers: Partial<AssignedUser>[] = [],
        saveDeltekUsers: Partial<AssignedUser>[] = [],
        assignedContactUsers: Partial<AssignedUser>[] = []
    ) => {
        const submissionStatus = handleSubmission(shouldSubmit)

        const sustainableMindUrl = productListingDetailForm.partnerships.value.find(
            (item) => item.name === 'Sustainable Minds'
        )

        const saveProductListingDetailInputVariable = {
            saveProductListingDetailInput: {
                buildingTypes:
                    productListingDetailForm.buildingTypes.value ?? [],
                catalogs: productListingDetailForm.catalogs.value,
                categories: productListingDetailForm.categories?.value,
                documents: productListingDetailForm.documents.value,
                firmId: mfrProductDetailUpdate.firmId,
                firstPublished: mfrProductDetailUpdate.firstPublished,
                images: productListingDetailForm.media.images,
                isActive: mfrProductDetailUpdate.isActive,
                isPremiumPlus: mfrProductDetailUpdate.isPremiumPlus,
                lastPublished: mfrProductDetailUpdate.lastPublished,
                listingId: mfrProductDetailUpdate.listingId,
                longDescription:
                    productListingDetailForm.productDescription.value ?? '',
                model: productListingDetailForm.model.value ?? '',
                partnershipLinks:
                    [
                        ...productListingDetailForm.partnerships.value.filter(
                            (each) =>
                                each.url !== '' &&
                                each.name !== 'Sustainable Minds'
                        ),
                        {
                            ...sustainableMindUrl,
                            url: !!validateFullUrlPath(
                                sustainableMindUrl?.url ?? ''
                            )
                                ? sustainableMindUrl?.url
                                : `${SUSTAINABLE_MIND_BASE_URL}/company/${sustainableMindUrl?.url}`,
                        },
                    ] ?? [],
                productName:
                    productListingDetailForm.productName.value?.trim() ?? '',
                productType: mfrProductDetailUpdate.productType,
                properties: getPropertiesInput(
                    productListingDetailForm.properties.normalizedProperties,
                    productListingDetailForm.properties.value
                ),
                sectionNumber: mfrProductDetailUpdate.sectionNumber,
                sectionTitle: mfrProductDetailUpdate.sectionTitle,
                submissionStatus,
                type: mfrProductDetailUpdate.type,
                videos: productListingDetailForm.media.videos,
                webLinks: productListingDetailForm.webLinks.value ?? [],
                assignedBPMUsers: savedBPMUsers ?? [],
                assignedDeltekUsers: saveDeltekUsers ?? [],
                assignedContactUsers,
                canAutoGenerateProductSpecification:
                    canAutoGeneratePSD &&
                    (mfrProductDetailUpdate.type === ListingLevel.Premium ||
                        mfrProductDetailUpdate.type ===
                            ListingLevel.PremiumMasterSpec ||
                        mfrProductDetailUpdate.type ===
                            ListingLevel.ProductListing),
                productDetailNotes: (Object.values(tempNotes) ?? []).map(
                    (note) => ({
                        content: note.longDescription,
                        listingId: productInfo.listingId,
                        productDetailNoteType: note.noteType,
                        reason: note.reason,
                    })
                ),
                contentLibrary:
                    productListingDetailForm.contentLibrary.value?.trim() ??
                    ContentLibrary.MasterSpec,
            } as ProductListingDetailInput,
        }

        if (submit && !shouldSubmit) {
            setBpmSave(true)
        }
        if (newListingRequest) {
            createProductListingDetail({
                variables: saveProductListingDetailInputVariable,
            })
        } else {
            saveProductListingDetail({
                variables: {
                    ...saveProductListingDetailInputVariable,
                },
            })
            if (shouldSubmit) {
                setEditMode(false)
                setBpmSave(false)
            }
        }
    }

    const handlePublishClick = () => {
        handleSaveClick(true)
    }

    const handleFieldChange = (
        field: ProductListingDetailFormField,
        value: string | string[]
    ) => {
        switch (field) {
            case 'buildingTypes':
                setProductListingDetailForm((past) => ({
                    ...past,
                    buildingTypes: {
                        ...past.buildingTypes,
                        value: (value as string[]) ?? [],
                        valid: value.length > 0 || (addProduct && !isBPM),
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    buildingTypes: (value as string[]) ?? [],
                })
                break
            case 'categories':
                setProductListingDetailForm((past) => ({
                    ...past,
                    categories: {
                        ...past.categories,
                        value: (value as string[]) ?? [],
                        valid: value.length > 0 || (addProduct && !isBPM),
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    categories: (value as string[]) ?? [],
                })
                break

            case 'description':
                setProductListingDetailForm((past) => ({
                    ...past,
                    productDescription: {
                        ...past.productDescription,
                        valid: value.length <= MAX_WORD_LIMIT,
                        value: (value as string) ?? '',
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    longDescription: (value as string) ?? '',
                })
                break
            case 'name':
                setProductListingDetailForm((past) => ({
                    ...past,
                    productName: {
                        ...past.productName,
                        value: (value as string) ?? '',
                        valid:
                            !!(value as string).trim() ||
                            (mfrProductDetailUpdate.submissionStatus ===
                                ListingStatus.Completed &&
                                mfrProductDetailUpdate.type ===
                                    ListingLevel.Manufacturer),
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    productName: (value as string) ?? '',
                })
                checkDuplicacy(value as string)
                break
            case 'model':
                setProductListingDetailForm((past) => ({
                    ...past,
                    model: {
                        ...past.model,
                        value: (value as string) ?? '',
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    model: (value as string) ?? '',
                })
                break
            case 'assignedContactUsers':
                setProductListingDetailForm((past) => ({
                    ...past,
                    assignedContactUsers: {
                        ...past.assignedContactUsers,
                        value: (value as string) ?? '',
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    assignedContactUsers: (value
                        ? JSON.parse(value as string)
                        : []) as Partial<User>[],
                })

                break
            case 'contentLibrary':
                setProductListingDetailForm((past) => ({
                    ...past,
                    contentLibrary: {
                        ...past.contentLibrary,
                        value: (value as string) ?? '',
                        valid: !!value,
                    },
                }))
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    contentLibrary: (value as ContentLibrary) ?? '',
                })

                break
        }
    }
    const handleBPMUsersChange = React.useCallback(
        (users: Partial<AssignedUser>[], valid: boolean) => {
            let data: Partial<AssignedUser>[] = []
            users.forEach((item) =>
                data.push({ userId: item.id, role: item.role })
            )
            users.length === data.length &&
                setProductListingDetailForm((past) => ({
                    ...past,
                    assignedBPMUsers: {
                        value: users,
                        valid: valid,
                    },
                }))
        },
        []
    )
    const handleDeltekUsersChange = React.useCallback(
        (users: Partial<AssignedUser>[], valid: boolean) => {
            let data: Partial<AssignedUser>[] = []
            users.forEach((item) =>
                data.push({ userId: item.id, role: item.role })
            )
            users.length === data.length &&
                setProductListingDetailForm((past) => ({
                    ...past,
                    assignedDeltekUsers: {
                        value: users,
                        valid: valid,
                    },
                }))
        },
        []
    )
    const handleWebLinksChange = (
        links: Partial<ListingDetailLinkInput>[],
        valid: boolean
    ) => {
        setProductListingDetailForm((past) => ({
            ...past,
            webLinks: {
                value: links,
                valid,
            },
        }))
        setMfrProductDetailUpdate({
            ...mfrProductDetailUpdate,
            webLinks: links?.map((each) => ({
                ...each,
            })) as Partial<ManufacturerProductWebLinkDetail>[],
        })
    }

    const handlePartnershipLinksChange = (
        links: Partial<ListingDetailPartnershipLinkInput>[],
        valid: boolean
    ) => {
        setProductListingDetailForm((past) => ({
            ...past,
            partnerships: {
                value: links.filter((each) => each.url !== ''),
                valid,
            },
        }))
        setMfrProductDetailUpdate({
            ...mfrProductDetailUpdate,
            partnershipLinks: links?.map(
                (each) => ({ ...each } as Partial<Partnership>)
            ) as Partial<Partnership>[],
        })
    }

    const handleMedia = (
        images: Partial<ListingDetailImageInput>[],
        videos: Partial<ListingDetailVideoInput>[]
    ) => {
        setProductListingDetailForm((past) => ({
            ...past,
            media: {
                images,
                videos,
                valid: true,
            },
        }))
        setMfrProductDetailUpdate({
            ...mfrProductDetailUpdate,
            images: images.map(
                (each) =>
                    ({ ...each } as Partial<ManufacturerProductImageDetail>)
            ),
            videos: videos.map(
                (each) =>
                    ({ ...each } as Partial<ManufacturerProductVideoDetail>)
            ),
        })
    }

    const handleDocumentsChange = (
        longDocType: Partial<ListingDetailDocumentInput>,
        shortDocType: Partial<ListingDetailDocumentInput>,
        catalog: Partial<ListingDetailCatalogInput>
    ) => {
        let docs: Partial<ListingDetailDocumentInput>[] | undefined = []
        let catalogs: Partial<ListingDetailCatalogInput>[] | undefined = []

        /*
         * For documents, add ids of ShortForm and FullLength to retain
         * For catalog, make undefined to retain
         * For delete, pass empty array for both
         */

        if (longDocType.title !== '') {
            if (
                longDocType.fileUploadDocument &&
                longDocType.fileUploadDocument?.fileName !== ''
            ) {
                docs?.push({ ...longDocType, id: undefined })
            } else {
                docs?.push(longDocType)
            }
        }

        if (shortDocType.title !== '') {
            if (
                shortDocType.fileUploadDocument &&
                shortDocType.fileUploadDocument?.fileName !== ''
            ) {
                docs?.push({ ...shortDocType, id: undefined })
            } else {
                docs?.push(shortDocType)
            }
        }

        if (
            shortDocType.id !== '' &&
            longDocType.id !== '' &&
            shortDocType.fileUploadDocument?.fileName === '' &&
            longDocType.fileUploadDocument?.fileName === ''
        ) {
            docs = productListingDetailForm.documents.value
        }

        if (catalog.title !== '') {
            if (
                catalog.fileUploadDocument &&
                catalog.fileUploadDocument?.fileName !== ''
            ) {
                catalogs = [catalog]
            } else {
                catalogs = undefined
            }
        } else {
            catalogs = []
        }

        setProductListingDetailForm((past) => ({
            ...past,
            catalogs: {
                value: catalogs,
                valid: true,
            },
            documents: {
                value: docs?.filter((doc) => !!doc.title),
                valid: true,
            },
        }))
        setMfrProductDetailUpdate({
            ...mfrProductDetailUpdate,
            catalogs: catalog.title !== '' ? [{ title: catalog.title }] : [],
            manufacturerDocs: docs
                ?.filter((doc) => !!doc.title)
                ?.map((each) => ({
                    docType: each.docType,
                    title: each.title,
                })),
        })
    }

    const handlePropertiesChange = useCallback(
        (value: SupportedOptionsObject) => {
            setProductListingDetailForm((past) => ({
                ...past,
                properties: {
                    ...past.properties,
                    value,
                },
            }))

            const arePropertiesDirty =
                !isEqual(
                    productListingDetailForm.properties.pristineValue,
                    value
                ) ||
                !isEqual(
                    productListingDetailForm.properties.normalizedProperties,
                    getNormalizedProperties(productInfo?.properties)
                )

            if (propertiesDirty !== arePropertiesDirty)
                setPropertiesDirty(arePropertiesDirty)
        },
        [
            propertiesDirty,
            productListingDetailForm,
            productInfo,
            setProductListingDetailForm,
        ]
    )

    const handleSelection = useCallback(
        (
            propertyName: string,
            subPropertyName: string,
            value: string
        ) => () => {
            const selected = productListingDetailForm.properties.value ?? []

            const alreadySelected = selected[propertyName][
                subPropertyName
            ]?.includes(value)

            const newValueArr = alreadySelected
                ? selected[propertyName][subPropertyName!]?.filter(
                      (d) => d !== value
                  )
                : [...selected[propertyName][subPropertyName!], value]

            handlePropertiesChange({
                ...selected,
                [propertyName]: {
                    ...(selected[propertyName] ?? {}),
                    [subPropertyName!]: newValueArr,
                },
            })
        },
        [productListingDetailForm.properties.value, handlePropertiesChange]
    )

    /**
     * This is a workaround for the filestack
     * "onOpen" delay. It starts a poll for the presence of
     * the filestack picker modal in the DOM, and sets
     * the filestackLoading state to false once the
     * element is found.
     */
    const checkPickerUi = () => {
        const hasPickerUi =
            document.getElementsByClassName('fsp-modal').length >= 1
        if (hasPickerUi) {
            setFilestackLoading(false)
        } else {
            setTimeout(() => checkPickerUi(), 500)
        }
    }

    const handleFilestackLoading = (isLoading: boolean | null) => {
        if (isLoading === false) {
            // Cover the "gap" by starting a poll
            // for the picker modal element in the DOM.
            checkPickerUi()
        } else {
            setFilestackLoading(true)
        }
    }

    const handleTransferringFiles = (value: boolean) =>
        setTransferringFiles(value)

    const handleHeaderChanges = (
        field: ListingHeaderVariants,
        value: string | boolean
    ) => {
        switch (field) {
            case 'state': {
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    submissionStatus: value as string,
                })
                setProductListingDetailForm((past) => ({
                    ...past,
                    productName: {
                        ...past.productName,
                        valid:
                            !!(past.productName.value as string).trim() ||
                            (value === ListingStatus.Completed &&
                                mfrProductDetailUpdate.type ===
                                    ListingLevel.Manufacturer),
                    },
                }))
                setDirty(true)
                break
            }
            case 'status': {
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    isActive: value as boolean,
                })
                setDirty(true)
                break
            }
            case 'listingLevel': {
                setMfrProductDetailUpdate({
                    ...mfrProductDetailUpdate,
                    type: value as ListingLevel,
                })
                setProductListingDetailForm((past) => ({
                    ...past,
                    productName: {
                        ...past.productName,
                        valid:
                            !!(past.productName.value as string).trim() ||
                            (mfrProductDetailUpdate.submissionStatus ===
                                ListingStatus.Completed &&
                                value === ListingLevel.Manufacturer),
                    },
                }))
                setDirty(true)
                break
            }
        }
    }

    const showListingHeader =
        isDeltekAdmin ||
        isDeltekPublishingAuthor ||
        isDeltekAuthor ||
        isDeltekReviewer ||
        isBPMListing ||
        isBPMReseller
    const showSpecificationDoc =
        (productInfo.type === ListingLevel.Premium ||
            productInfo.type === ListingLevel.PremiumMasterSpec ||
            productInfo.type === ListingLevel.ProductListing) &&
        AUTOGEN_ELIGIBLE_STATUSES.has(productInfo.submissionStatus ?? '') &&
        !getDocDetail(productInfo.manufacturerDocs, 'ShortForm') &&
        !getDocDetail(productInfo.manufacturerDocs, 'FullLength')
    const checkDuplicacy = (
        productName: string = mfrProductDetailUpdate?.productName ?? ''
    ) => {
        const facets = [
            {
                name: 'listingLevels',
                values: !mfrProductDetailUpdate.type
                    ? [productInfo?.type]
                    : mfrProductDetailUpdate.type === ListingLevel.Premium
                    ? [
                          ListingLevel.Manufacturer,
                          ListingLevel.Premium,
                          ListingLevel.PremiumMasterSpec,
                      ]
                    : [mfrProductDetailUpdate.type],
            },
            {
                name: 'productType',
                values: [mfrProductDetailUpdate.productType ?? ''],
            },
            {
                name: 'firmName',
                values: [`${productInfo?.firmName}`],
            },
            {
                name: 'sectionNames',
                values: [productInfo?.sectionName ?? ''],
            },
            {
                name: 'productName',
                values: [productName?.trim() ?? ''],
            },
        ]
        getAllProducts({
            variables: {
                manufacturerProductSearchInput: {
                    facets,
                    logEventSource: LogEventSource.CreateListingValidation,
                },
            },
        })
    }

    const executePSDQuery = () => {
        if (
            productInfo.listingId &&
            (showSpecificationDoc ||
                productInfo.canAutoGenerateProductSpecification)
        )
            generateProductSpecification({
                variables: { listingId: productInfo.listingId },
            })
    }

    const setProductDetailFormProperties = useCallback(
        (
            properties: ProductListingDetailForm['properties']['normalizedProperties'],
            value: SupportedOptionsObject
        ) => {
            setProductListingDetailForm((form: ProductListingDetailForm) => ({
                ...form,
                properties: {
                    ...form.properties,
                    normalizedProperties: properties,
                    value,
                },
            }))

            setMfrProductDetailUpdate({
                ...mfrProductDetailUpdate,
                properties: getPropertiesInput(
                    productListingDetailForm.properties.normalizedProperties,
                    productListingDetailForm.properties.value
                ),
            })
        },
        [productListingDetailForm.properties, mfrProductDetailUpdate]
    )

    useEffect(() => {
        if (allProductsName) {
            const existingProducts = allProductsName?.listingSearch?.matches?.map(
                (p) => p.productName
            )
            const isDuplicate =
                productListingDetailForm.productName.value &&
                (newListingRequest
                    ? existingProducts?.includes(
                          productListingDetailForm.productName.value
                      )
                    : existingProducts
                          ?.filter((p) => p !== productInfo.productName)
                          .includes(productListingDetailForm.productName.value))
            setDuplicateName(!!isDuplicate)
        }
    }, [
        allProductsName,
        newListingRequest,
        productInfo.productName,
        productListingDetailForm,
    ])

    useEffect(() => {
        if (
            productInfo.listingId &&
            (showSpecificationDoc ||
                productInfo.canAutoGenerateProductSpecification) &&
            prodSpecificationData?.generateProductSpecification?.downloadURI
        )
            navigateToExternalLink(
                prodSpecificationData?.generateProductSpecification?.downloadURI
            )
        else if (
            productInfo.listingId &&
            (showSpecificationDoc ||
                productInfo.canAutoGenerateProductSpecification) &&
            prodSpecificationData?.generateProductSpecification?.errorMessage
        ) {
            showGlobalSnackbar({
                variables: {
                    message: 'Product Specification Document not Available',
                    variant: 'error',
                    id: `PSD_Error`,
                },
            })
        }
    }, [
        prodSpecificationData,
        productInfo,
        showGlobalSnackbar,
        showSpecificationDoc,
    ])

    // sets dirty when mfrProductDetailUpdate changes
    useEffect(() => {
        if (isDeltekPublishingAuthor) {
            if (dirty) {
                handlePublishClick()
                setDirty(false)
            }
        } else {
            const isDirty =
                (!newListingRequest &&
                    (!isEqual(productInfo, mfrProductDetailUpdate) ||
                        !isEqual(
                            productInfo.assignedBPMUsers,
                            productListingDetailForm.assignedBPMUsers.value
                        ))) ||
                (newListingRequest &&
                    !isEqual(
                        mfrProductDetailUpdate,
                        newListingDataRef.current
                    )) ||
                canAutoGeneratePSD !==
                    !!productInfo.canAutoGenerateProductSpecification
            if (dirty !== isDirty) setDirty(isDirty)
        }
    }, [
        productInfo,
        mfrProductDetailUpdate,
        dirty,
        newListingDataRef,
        canAutoGeneratePSD,
        newListingRequest,
        productListingDetailForm,
    ])

    // This must be done after mounting and no rerender happening
    useEffect(() => {
        if (hasMountedRef.current) {
            const targetIndex = +(queryParams.get('preview') ?? -1)
            const totalMedia =
                (productInfo?.images?.length || 0) +
                (productInfo?.videos?.length || 0)

            if (
                carouselRef?.current &&
                targetIndex > 0 &&
                targetIndex < totalMedia &&
                carouselRef?.current?.getCurrentIndex() !== targetIndex
            ) {
                carouselRef?.current?.slideToIndex(targetIndex)
            }
        }
        hasMountedRef.current = true
    }, [carouselRef, hasMountedRef, queryParams, productInfo])

    return (
        <ProductContext.Provider
            value={{
                product: productInfo,
                properties:
                    productListingDetailForm.properties.normalizedProperties,
                setProductDetailFormProperties,
                handleSelection,
                selectedData: productListingDetailForm.properties.value,
                setSelectedData: setProductListingDetailForm,
                updateListingStates,
                firmContact: designatedAccountAdminContact,
                isPremiumAuthorized:
                    !!isDeltekAdmin ||
                    !!isDeltekPublishingAuthor ||
                    !!isDeltekAuthor ||
                    !!(isBPMAdmin && hasManufacturerOptions),
                isManufacturerOptionsReadOnly: !!isBPMAdmin,
            }}
        >
            <NavPrompt
                showPrompt={(dirty || bpmSave || propertiesDirty) && editMode}
                title="Listing Detail Page"
            />
            {showListingHeader && (
                <GridContainer
                    className={clsx(
                        classes.header,
                        classes.basicListing,
                        'listingHeader'
                    )}
                    data-testid="header"
                >
                    <ListingHeader
                        onAddProductClick={handleAddProductClick}
                        isAddProductActive={isAddProductActive}
                        editMode={editMode || editResellerMode}
                        firmId={firmId}
                        isActive={
                            !dirty
                                ? productInfo.isActive
                                : mfrProductDetailUpdate.isActive
                        }
                        listingLevel={
                            mfrProductDetailUpdate.type ?? productInfo.type
                        }
                        onCancelClick={handleCancelClick}
                        onEditClick={handleEditClick}
                        onPublishClick={handlePublishClick}
                        onSaveClick={() => handleSaveClick(!submit)}
                        onHeaderChanges={handleHeaderChanges}
                        submissionStatus={
                            !dirty
                                ? productInfo.submissionStatus
                                : mfrProductDetailUpdate.submissionStatus
                        }
                        isDeprecated={
                            productInfo.submissionStatus ===
                            ListingStatus.Deprecated
                        }
                        assignment="None"
                        saveProductListingDetailLoading={
                            saveProductListingDetailLoading ||
                            transferringFiles ||
                            newProductListingDetailLoading ||
                            saveSwatchboxLoading
                        }
                        disableSave={hasErrors || checkingDuplicacy}
                        assignedBPM={
                            (newListingRequest && []) ||
                            productInfo?.assignedBPMUsers
                        }
                        assignedDeltek={
                            (newListingRequest && []) ||
                            productInfo?.assignedDeltekUsers
                        }
                        allBPMUsers={firmUsersData?.firm?.users ?? []}
                        allDeltekUsers={allUsersData?.getAllUsers?.users ?? []}
                        productInfo={
                            (newListingRequest && mfrProductDetailUpdate) ||
                            productInfo
                        }
                        handleBPMUsersChange={handleBPMUsersChange}
                        handleDeltekUsersChange={handleDeltekUsersChange}
                        roleBasedAccess={roleBasedAccess}
                        productListingDetailFormProp={productListingDetailForm}
                    />
                </GridContainer>
            )}
            <GridContainer alignItems="flex-start">
                {isBasic ? (
                    mFGOnly && !mFG ? (
                        <GridItem
                            md={7}
                            sm={12}
                            className={clsx(
                                classes.basicListing,
                                classes.basicListContainer
                            )}
                            data-testid="basic-detail-container"
                        >
                            {ListingLevel.Manufacturer} Listing
                        </GridItem>
                    ) : (
                        <GridItem
                            container
                            justifyContent="center"
                            md={7}
                            xs={12}
                            className={classes.imageGalleryFlexContainer}
                            data-testid="ehanced-premium-detail-container"
                        >
                            <ListingDetailMediaCarousel
                                editMode={editMode}
                                cancelled={cancelled}
                                images={
                                    (newListingRequest && []) ||
                                    productInfo.images
                                }
                                videos={
                                    (newListingRequest && []) ||
                                    productInfo.videos
                                }
                                handleMedia={handleMedia}
                                ref={carouselRef}
                            />

                            <GridContainer
                                className={classes.linksContainer}
                                spacing={2}
                            >
                                <GridItem xs={12} sm={6}>
                                    {editMode ? (
                                        <EditDocuments
                                            currentUserInfo={
                                                currentUserInfoResponse?.currentUserInfo
                                            }
                                            catalog={
                                                (newListingRequest && {}) ||
                                                productInfo.catalogs?.[0]
                                            }
                                            longDocType={
                                                (newListingRequest && {}) ||
                                                getDocDetail(
                                                    productInfo.manufacturerDocs,
                                                    'FullLength'
                                                )
                                            }
                                            shortDocType={
                                                (newListingRequest && {}) ||
                                                getDocDetail(
                                                    productInfo.manufacturerDocs,
                                                    'ShortForm'
                                                )
                                            }
                                            handleDocumentsChange={
                                                handleDocumentsChange
                                            }
                                            handleFilestackLoading={
                                                handleFilestackLoading
                                            }
                                            handleTransferringFiles={
                                                handleTransferringFiles
                                            }
                                        />
                                    ) : (
                                        <ViewDocuments
                                            level={productInfo.type}
                                            catalog={productInfo.catalogs?.[0]}
                                            longDocType={getDocDetail(
                                                productInfo.manufacturerDocs,
                                                'FullLength'
                                            )}
                                            shortDocType={getDocDetail(
                                                productInfo.manufacturerDocs,
                                                'ShortForm'
                                            )}
                                        />
                                    )}

                                    {editMode ? (
                                        <EditPartnerships
                                            pastPartnerships={
                                                (newListingRequest && []) ||
                                                productInfo.partnershipLinks
                                            }
                                            handlePartnershipLinksChange={
                                                handlePartnershipLinksChange
                                            }
                                            showSwatchbox={
                                                !!Number(
                                                    processEnv.REACT_APP_ShowSwatchbox
                                                )
                                            }
                                        />
                                    ) : editResellerMode ? (
                                        <EditPartnershipSwatchbox
                                            pastPartnerships={
                                                (newListingRequest && []) ||
                                                productInfo.partnershipLinks
                                            }
                                            handlePartnershipLinksChange={
                                                handlePartnershipLinksChange
                                            }
                                        />
                                    ) : (
                                        <ViewPartnerships
                                            level={productInfo.type}
                                            partnerships={
                                                productInfo.partnershipLinks
                                            }
                                            showSwatchbox={
                                                !!Number(
                                                    processEnv.REACT_APP_ShowSwatchbox
                                                )
                                            }
                                        />
                                    )}
                                </GridItem>
                                <GridItem xs={12} sm={6}>
                                    {editMode ? (
                                        <EditLinks
                                            pastLinks={
                                                (newListingRequest && []) ||
                                                productInfo.webLinks
                                            }
                                            handleWebLinksChange={
                                                handleWebLinksChange
                                            }
                                        />
                                    ) : (
                                        <ViewLinks
                                            level={productInfo.type}
                                            webLinks={productInfo.webLinks}
                                        />
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    ) //mfg ends
                ) : (
                    <GridItem
                        container
                        justifyContent="center"
                        md={7}
                        xs={12}
                        className={classes.imageGalleryFlexContainer}
                        data-testid="ehanced-premium-detail-container"
                    >
                        <ListingDetailMediaCarousel
                            editMode={editMode}
                            cancelled={cancelled}
                            images={
                                (newListingRequest && []) || productInfo.images
                            }
                            videos={
                                (newListingRequest && []) || productInfo.videos
                            }
                            handleMedia={handleMedia}
                            ref={carouselRef}
                        />

                        <GridContainer
                            className={classes.linksContainer}
                            spacing={2}
                        >
                            <GridItem lg={7} xl={6}>
                                {editMode ? (
                                    <EditDocuments
                                        isListingTypeValidForPSDBtn={
                                            mfrProductDetailUpdate.type ===
                                                ListingLevel.Premium ||
                                            mfrProductDetailUpdate.type ===
                                                ListingLevel.PremiumMasterSpec ||
                                            mfrProductDetailUpdate.type ===
                                                ListingLevel.ProductListing
                                        }
                                        currentUserInfo={
                                            currentUserInfoResponse?.currentUserInfo
                                        }
                                        catalog={
                                            (newListingRequest && {}) ||
                                            productInfo.catalogs?.[0]
                                        }
                                        longDocType={
                                            (newListingRequest && {}) ||
                                            getDocDetail(
                                                productInfo.manufacturerDocs,
                                                'FullLength'
                                            )
                                        }
                                        shortDocType={
                                            (newListingRequest && {}) ||
                                            getDocDetail(
                                                productInfo.manufacturerDocs,
                                                'ShortForm'
                                            )
                                        }
                                        handleDocumentsChange={
                                            handleDocumentsChange
                                        }
                                        handleFilestackLoading={
                                            handleFilestackLoading
                                        }
                                        handleTransferringFiles={
                                            handleTransferringFiles
                                        }
                                        showProductSpecification={
                                            canAutoGeneratePSD
                                        }
                                        setAutoGeneratePSD={
                                            setCanAutoGeneratePSD
                                        }
                                    />
                                ) : (
                                    <ViewDocuments
                                        level={productInfo.type}
                                        catalog={productInfo.catalogs?.[0]}
                                        specificationDocLoading={spfDocLoading}
                                        showSpecificationDoc={
                                            showSpecificationDoc &&
                                            !!productInfo.canAutoGenerateProductSpecification
                                        }
                                        executePSDQuery={executePSDQuery}
                                        longDocType={getDocDetail(
                                            productInfo.manufacturerDocs,
                                            'FullLength'
                                        )}
                                        shortDocType={getDocDetail(
                                            productInfo.manufacturerDocs,
                                            'ShortForm'
                                        )}
                                    />
                                )}

                                {editMode ? (
                                    <EditPartnerships
                                        pastPartnerships={
                                            (newListingRequest && []) ||
                                            productInfo.partnershipLinks
                                        }
                                        handlePartnershipLinksChange={
                                            handlePartnershipLinksChange
                                        }
                                        showSwatchbox={
                                            !!Number(
                                                processEnv.REACT_APP_ShowSwatchbox
                                            )
                                        }
                                    />
                                ) : editResellerMode ? (
                                    <EditPartnershipSwatchbox
                                        pastPartnerships={
                                            (newListingRequest && []) ||
                                            productInfo.partnershipLinks
                                        }
                                        handlePartnershipLinksChange={
                                            handlePartnershipLinksChange
                                        }
                                    />
                                ) : (
                                    <ViewPartnerships
                                        level={productInfo.type}
                                        partnerships={
                                            productInfo.partnershipLinks
                                        }
                                        showSwatchbox={
                                            !!Number(
                                                processEnv.REACT_APP_ShowSwatchbox
                                            )
                                        }
                                    />
                                )}
                            </GridItem>
                            <GridItem lg={5} xl={6}>
                                {editMode ? (
                                    <EditLinks
                                        pastLinks={
                                            (newListingRequest && []) ||
                                            productInfo.webLinks
                                        }
                                        handleWebLinksChange={
                                            handleWebLinksChange
                                        }
                                    />
                                ) : (
                                    <ViewLinks
                                        level={productInfo.type}
                                        webLinks={productInfo.webLinks}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                )}
                <GridItem md={12} xs={12} lg={5}>
                    <ProductDetails
                        editMode={editMode}
                        roleBasedAccess={roleBasedAccess}
                        isProductDuplicate={duplicateName}
                        productInfo={
                            (!newListingRequest && productInfo) ||
                            mfrProductDetailUpdate
                        }
                        userId={
                            currentUserInfoResponse?.currentUserInfo.kpUserId ??
                            ''
                        }
                        roles={{
                            isDeltekUser,
                            isDeltekAdmin,
                            isDeltekPublishingAuthor,
                            isDeltekAuthor,
                            isBPMAdmin,
                            isBPMAuthor,
                            isGuestBPMReseller,
                        }}
                        newListingRequest={newListingRequest}
                        handlePropertiesChange={handlePropertiesChange}
                        productListingDetailForm={productListingDetailForm}
                        handleFieldChange={handleFieldChange}
                        showListingInfo={isDeltekUser || isBPM}
                        designatedAccountAdminContact={
                            designatedAccountAdminContact
                        }
                        allBPMUsers={firmUsersData?.firm?.users ?? []}
                    />
                </GridItem>
            </GridContainer>
            <Backdrop
                className={classes.backdrop}
                open={
                    !!saveProductListingDetailLoading ||
                    filestackLoading ||
                    newProductListingDetailLoading ||
                    !!saveSwatchboxLoading
                }
            >
                {!filestackLoading && <CircularProgress color="inherit" />}
            </Backdrop>
        </ProductContext.Provider>
    )
}
